import { financeMoney } from "./financeMoney";
import { useEffect, useState } from "react";
import { academyRequest } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import type { FinanceProjection } from "./generated/finance";

const routes: Record<string, string> = {
  "finance-overview": "invoices",
  "finance-invoices": "invoices",
  "finance-usage": "usage",
  "finance-renewal": "renewal",
  "funding-budgets": "funding",
  procurement: "procurement",
  billing: "invoices",
};
export const isFinanceDirectory = (route: string) => Boolean(routes[route]);
export function useFinanceDirectory(route: string) {
  const identity = useIdentity();
  const kind = routes[route];
  const sessionPresent = Boolean(identity.session);
  const context = `${identity.session?.tenantId}:${identity.session?.workspace.id}:${identity.session?.user.id}:${kind}`;
  const [state, setState] = useState<{ context: string; data?: FinanceProjection; error?: string } | null>(
    null,
  );
  useEffect(() => {
    if (!kind || !sessionPresent) return;
    const abort = new AbortController();
    void (async () => {
      try {
        const before = await checkIdentitySession();
        const data = await academyRequest<FinanceProjection>(
          `finance/context/${kind}`,
          undefined,
          "GET",
          true,
          undefined,
          abort.signal,
        );
        const after = await checkIdentitySession();
        if (
          before.user.id !== after.user.id ||
          before.tenantId !== after.tenantId ||
          before.workspace.id !== after.workspace.id
        )
          throw new Error("Your finance workspace changed. Reload to continue.");
        if (!abort.signal.aborted) setState({ context, data });
      } catch (cause) {
        if (!abort.signal.aborted)
          setState({
            context,
            error: cause instanceof Error ? cause.message : "Unable to load finance records.",
          });
      }
    })();
    return () => abort.abort();
  }, [context, kind, sessionPresent]);
  const current = state?.context === context ? state : null;
  const rows: Record<string, string | number | boolean>[] = (current?.data?.items || []).map(item => ({
    id: String(item.id),
    name: String(item.reference || item.id),
    reference: String(item.reference || item.id),
    status: String(item.status || "Recorded"),
    amount: financeMoney(String(item.totalMinor), String(item.currency)),
    value: financeMoney(String(item.outstandingMinor ?? item.totalMinor), String(item.currency)),
    date: item.dueAt ? new Date(String(item.dueAt)).toLocaleDateString() : "No contractual date",
    owner: "Current billing account",
    source: "Academy finance",
    progress: "Not applicable",
  }));
  return {
    enabled: Boolean(kind),
    rows,
    loading: Boolean(kind && !current && identity.session),
    error: current?.error || (!identity.session ? "Sign in to load finance records." : ""),
    notice: current?.data?.notice || "Loading current finance records.",
    data: current?.data,
  };
}
